import { useCallback, useState } from "react";
import { Link, Outlet } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { DIRECTUS_URL } from "../config";
import { useClassrooms } from "../data/ClassroomContext";
import useNotifications from "../data/useNotifications";
import useOnlineStatus from "../hooks/useOnlineStatus";
import NotificationsPanel from "./NotificationsPanel";
import WorkspaceNav from "./WorkspaceNav";
import { BellIcon, LogoutIcon, OfflineIcon } from "./Icons";
import ui from "./ui.module.css";

const DEFAULT_AVATAR = "/avatar.svg";

const avatarUrl = (avatarId) =>
  avatarId
    ? `${DIRECTUS_URL}/assets/${avatarId}?width=96&height=96&fit=cover`
    : DEFAULT_AVATAR;

// A layout route; also wraps a page directly when given children.
const AppShell = ({ children }) => {
  const { user, signOut } = useAuth();
  const classes = useClassrooms();
  const online = useOnlineStatus();
  const notifications = useNotifications();
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [seenBefore, setSeenBefore] = useState(null);

  const closeNotifications = useCallback(() => setNotificationsOpen(false), []);

  const openNotifications = () => {
    setSeenBefore(notifications.lastSeen);
    notifications.markSeen();
    setNotificationsOpen(true);
  };

  const name = [user.firstName, user.lastName].filter(Boolean).join(" ");

  return (
    <>
      <a href="#main" className={ui.skipLink}>
        Skip to content
      </a>
      <header className={ui.header}>
        <div className={`container ${ui.headerInner}`}>
          <Link to="/" aria-label="Today">
            <img src="/thutotime-logo.svg" alt="" className={ui.logo} />
          </Link>

          <div className={ui.userArea}>
            {!online && (
              <span className={ui.pill} role="status">
                <OfflineIcon size={16} />
                Offline
              </span>
            )}
            <div className={`d-none d-md-block ${ui.welcome}`}>
              <strong>Welcome</strong>
              {name && <span>{name}</span>}
            </div>
            <img
              className={`d-none d-sm-block ${ui.avatar}`}
              src={avatarUrl(user.avatarId)}
              alt=""
              onError={(event) => {
                if (!event.currentTarget.src.endsWith(DEFAULT_AVATAR)) {
                  event.currentTarget.src = DEFAULT_AVATAR;
                }
              }}
            />
            <button
              type="button"
              className={ui.iconButton}
              onClick={openNotifications}
              aria-label={
                notifications.hasUnseen ? "Notifications, new" : "Notifications"
              }
            >
              <BellIcon size={24} />
              {notifications.hasUnseen && <span className={ui.badgeDot} />}
            </button>
            <button
              type="button"
              className={ui.iconButton}
              onClick={signOut}
              aria-label="Sign out"
            >
              <LogoutIcon size={24} />
            </button>
          </div>
        </div>
        {classes?.active && <WorkspaceNav />}
      </header>

      <main id="main">{children || <Outlet />}</main>

      {notificationsOpen && (
        <NotificationsPanel
          notifications={notifications}
          seenBefore={seenBefore}
          onClose={closeNotifications}
        />
      )}
    </>
  );
};

export default AppShell;
